import React, { useState, useEffect, useRef } from 'react';
import {
    Upload, Trash2, Download, FileText, FileImage,
    File, AlertCircle, CheckCircle, Loader, Eye
} from 'lucide-react';
import api from '../../services/api';

const PRIMARY      = '#2563EB';
const PRIMARY_DARK = '#1D4ED8';

const MIME_CFG = {
    'application/pdf':   { label: 'PDF',  color: '#e74c3c', icon: FileText  },
    'image/jpeg':        { label: 'JPG',  color: '#3498db', icon: FileImage },
    'image/png':         { label: 'PNG',  color: '#2ecc71', icon: FileImage },
    'image/gif':         { label: 'GIF',  color: '#9b59b6', icon: FileImage },
    'application/msword':{ label: 'DOC',  color: '#2980b9', icon: FileText  },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                         { label: 'DOCX', color: '#2980b9', icon: FileText  },
    'application/vnd.ms-excel':
                         { label: 'XLS',  color: '#27ae60', icon: FileText  },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                         { label: 'XLSX', color: '#27ae60', icon: FileText  },
    'text/plain':        { label: 'TXT',  color: '#95a5a6', icon: File      },
};

const ALLOWED_MIMES = Object.keys(MIME_CFG);
const MAX_MB = 10;
const MAX_B  = MAX_MB * 1024 * 1024;

const fmtSize = (b) => b < 1024 ? b + ' B' : b < 1048576 ? (b / 1024).toFixed(1) + ' KB' : (b / 1048576).toFixed(1) + ' MB';
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d || '—'; } };
const canPreview = (mime) => mime?.startsWith('image/') || mime === 'application/pdf';

const FileIcon = ({ mime, size = 18 }) => {
    const cfg  = MIME_CFG[mime];
    const Icon = cfg?.icon || File;
    return <Icon size={size} color={cfg?.color || PRIMARY} />;
};

const TypeBadge = ({ mime }) => {
    const cfg = MIME_CFG[mime] || { label: 'FILE', color: PRIMARY };
    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px',
            background: cfg.color + '18', color: cfg.color,
            borderRadius: 4, fontSize: 11, fontWeight: 700,
            border: `1px solid ${cfg.color}44`, letterSpacing: '0.04em',
        }}>{cfg.label}</span>
    );
};

const DocumentsTab = ({ jobId }) => {
    const [docs,       setDocs]       = useState([]);
    const [docTypes,   setDocTypes]   = useState([]);
    const [loading,    setLoading]    = useState(false);
    const [typesLoading, setTypesLoading] = useState(true);
    const [uploading,  setUploading]  = useState(false);
    const [dragOver,   setDragOver]   = useState(false);
    const [errMsg,     setErrMsg]     = useState('');
    const [okMsg,      setOkMsg]      = useState('');

    const [selFile,    setSelFile]    = useState(null);
    const [docTypeId,  setDocTypeId]  = useState('');
    const [remarks,    setRemarks]    = useState('');
    const fileRef = useRef(null);

    useEffect(() => { loadDocTypes(); }, []);
    useEffect(() => { if (jobId) loadDocs(); }, [jobId]);

    const loadDocTypes = async () => {
        setTypesLoading(true);
        try {
            const res = await api.get('/DocTypeMaster.php?active=1');
            if (res.data.success) setDocTypes(res.data.data || []);
        } catch (e) { showErr('Could not load document types: ' + e.message); }
        finally { setTypesLoading(false); }
    };

    const loadDocs = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/JobDocuments.php/${jobId}`);
            if (res.data.success) setDocs(res.data.data || []);
        } catch (e) { showErr('Failed to load documents: ' + e.message); }
        finally { setLoading(false); }
    };

    const showErr = (m) => { setErrMsg(m);  setTimeout(() => setErrMsg(''), 4500); };
    const showOk  = (m) => { setOkMsg(m);   setTimeout(() => setOkMsg(''),  3000); };

    const validate = (file) => {
        if (!MIME_CFG[file.type]) { showErr(`File type not allowed: ${file.type}`); return false; }
        if (file.size > MAX_B)    { showErr(`File exceeds ${MAX_MB} MB (${fmtSize(file.size)})`); return false; }
        return true;
    };
    const pickFile = (file) => { if (file && validate(file)) { setSelFile(file); setErrMsg(''); } };
    const clearFile = (e) => { e.stopPropagation(); setSelFile(null); if (fileRef.current) fileRef.current.value = ''; };

    const handleUpload = async () => {
        if (!selFile)   { showErr('Select a file first.'); return; }
        if (!docTypeId) { showErr('Select a document type.'); return; }
        if (!jobId)     { showErr('Save the job before uploading.'); return; }
        try {
            setUploading(true);
            const fd = new FormData();
            fd.append('file', selFile); fd.append('jobId', jobId); fd.append('docTypeId', docTypeId); fd.append('remarks', remarks);
            const res = await api.post('/JobDocuments.php', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (res.data.success) {
                showOk('Document uploaded successfully.');
                setSelFile(null); setDocTypeId(''); setRemarks('');
                if (fileRef.current) fileRef.current.value = '';
                loadDocs();
            } else throw new Error(res.data.message);
        } catch (e) { showErr('Upload failed: ' + (e.response?.data?.message || e.message)); }
        finally { setUploading(false); }
    };

    const handleDownload = async (doc) => {
        try {
            const res = await api.get(`/JobDocuments.php/download/${doc.DocumentID}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url; a.download = doc.OriginalName || doc.FileName; a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) { showErr('Download failed: ' + e.message); }
    };

    const handlePreview = async (doc) => {
        try {
            const res = await api.get(`/JobDocuments.php/download/${doc.DocumentID}`, { responseType: 'blob' });
            window.open(window.URL.createObjectURL(new Blob([res.data], { type: doc.MimeType })), '_blank');
        } catch (e) { showErr('Preview failed: ' + e.message); }
    };

    const handleDelete = async (doc) => {
        if (!window.confirm(`Delete "${doc.OriginalName || doc.FileName}"? This cannot be undone.`)) return;
        try {
            const res = await api.delete(`/JobDocuments.php/${doc.DocumentID}`);
            if (res.data.success) { showOk('Deleted.'); loadDocs(); }
            else throw new Error(res.data.message);
        } catch (e) { showErr('Delete failed: ' + e.message); }
    };

    const inpCls = 'w-full px-2.5 py-2 border-[1.5px] border-[#CBD5E1] rounded-md text-[13px] font-[inherit] bg-white outline-none transition-all duration-150 focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.1)] box-border';

    return (
        <div className="p-5 bg-white flex flex-col gap-4 min-h-[400px]">
            <style>{`
              @keyframes docs-fadein { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
              @keyframes docs-spin { to{transform:rotate(360deg)} }
            `}</style>

            {/* Toasts */}
            {errMsg && <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-medium" style={{ background:'#FEE2E2', color:'#B91C1C', borderLeft:'4px solid #DC2626', animation:'docs-fadein 0.2s ease' }}><AlertCircle size={15}/> {errMsg}</div>}
            {okMsg  && <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-medium" style={{ background:'#DCFCE7', color:'#15803D', borderLeft:'4px solid #16A34A', animation:'docs-fadein 0.2s ease' }}><CheckCircle size={15}/> {okMsg}</div>}

            {/* Upload panel */}
            {!jobId ? (
                <div className="flex flex-col items-center gap-2.5 px-7 py-7 rounded-lg text-center text-sm" style={{ background:'#FFFBEB', border:'1px dashed #F59E0B', color:'#B45309' }}>
                    <AlertCircle size={30} color="#F59E0B"/>
                    <p className="m-0">Save the job first before attaching documents.</p>
                </div>
            ) : (
                <div className="px-5 py-4 rounded-lg" style={{ background:'#F8FAFC', border:'1px solid #E2E8F0' }}>
                    <div className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wide">Upload New Document</div>

                    {/* Drop zone */}
                    <div
                        className={`border-2 border-dashed rounded-lg px-5 py-7 text-center cursor-pointer transition-all duration-200 flex flex-col items-center gap-2 mb-3.5 ${
                          selFile ? 'border-[#16A34A] bg-[#F0FDF4] border-solid' :
                          dragOver ? 'border-[#2563EB] bg-[#EFF6FF] shadow-[0_0_0_4px_rgba(37,99,235,0.12)]' :
                          'border-[#93C5FD] bg-white hover:border-[#2563EB] hover:bg-[#EFF6FF]'}`}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files[0]); }}
                        onClick={() => fileRef.current?.click()}
                    >
                        <input ref={fileRef} type="file" accept={ALLOWED_MIMES.join(',')} className="hidden" onChange={(e) => pickFile(e.target.files[0])}/>
                        {selFile ? (
                            <div className="flex flex-col items-center gap-1.5">
                                <FileIcon mime={selFile.type} size={34}/>
                                <div className="text-sm font-semibold text-gray-800 break-all">{selFile.name}</div>
                                <div className="text-xs text-gray-500">{fmtSize(selFile.size)}</div>
                                <button onClick={clearFile} className="mt-1 px-3 py-1 rounded-md text-xs font-semibold cursor-pointer transition-colors duration-150" style={{ background:'#FEE2E2', color:'#DC2626', border:'1px solid #FECACA' }}>× Remove</button>
                            </div>
                        ) : (
                            <>
                                <Upload size={38} color={PRIMARY}/>
                                <p className="m-0 text-sm text-gray-700"><strong>Click to browse</strong> or drag &amp; drop here</p>
                                <p className="m-0 text-xs text-gray-400">PDF, Images, Word, Excel, TXT · max {MAX_MB} MB</p>
                            </>
                        )}
                    </div>

                    {/* Meta row */}
                    <div className="grid gap-3 items-end max-[768px]:grid-cols-1" style={{ gridTemplateColumns:'220px 1fr auto' }}>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                Document Type <span className="text-red-500">*</span>
                                {typesLoading && <span className="text-[11px] text-gray-400 italic"> Loading…</span>}
                            </label>
                            {docTypes.length === 0 && !typesLoading ? (
                                <div className="text-xs rounded-md px-3 py-2" style={{ background:'#FFFBEB', border:'1px solid #FCD34D', color:'#B45309' }}>
                                    No document types found.{' '}
                                    <a href="/masters/document-types" target="_blank" rel="noreferrer" className="font-semibold" style={{ color:PRIMARY }}>Add document types</a>
                                </div>
                            ) : (
                                <select value={docTypeId} onChange={(e) => setDocTypeId(e.target.value)} disabled={typesLoading} className={inpCls + ' cursor-pointer'}>
                                    <option value="">— Select type —</option>
                                    {docTypes.map(dt => <option key={dt.DocTypeID} value={dt.DocTypeID}>{dt.DocTypeName}</option>)}
                                </select>
                            )}
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Remarks</label>
                            <input type="text" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Optional note about this document" maxLength={255} className={inpCls}/>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs invisible">&nbsp;</label>
                            <button onClick={handleUpload} disabled={uploading || !selFile || !docTypeId}
                                className="inline-flex items-center gap-1.5 px-4 py-2 border-0 rounded-lg text-[13px] font-semibold cursor-pointer text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                                style={{ background:PRIMARY }}
                                onMouseEnter={(e) => { if (!uploading && selFile && docTypeId) e.currentTarget.style.background = PRIMARY_DARK; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = PRIMARY; }}>
                                {uploading ? <><Loader size={15} style={{ animation:'docs-spin 0.8s linear infinite' }}/> Uploading…</> : <><Upload size={15}/> Upload</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Document list */}
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-[#E2E8F0]">
                    <span className="text-sm font-bold text-gray-800 uppercase tracking-wide">Attached Documents</span>
                    <span className="inline-block px-2.5 py-0.5 text-white rounded-full text-xs font-bold" style={{ background:PRIMARY }}>{docs.length} file{docs.length !== 1 ? 's' : ''}</span>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center gap-3 py-12 px-5 text-gray-400 text-[13px] text-center">
                            <div className="w-8 h-8 border-[3px] border-gray-200 rounded-full" style={{ borderTopColor:PRIMARY, animation:'docs-spin 0.75s linear infinite' }}/>
                            <p>Loading…</p>
                        </div>
                    ) : docs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-3 py-12 px-5 text-gray-400 text-[13px] text-center">
                            <File size={42} color="#d1d5db"/>
                            <p>No documents attached yet.</p>
                        </div>
                    ) : (
                        <table className="w-full border-collapse text-[13px]">
                            <thead className="sticky top-0 z-[5] text-white" style={{ background:'#1E293B' }}>
                                <tr>
                                    {[['#',40],['File',55],['File Name',null],['Document Type',190],['Size',80],['Date',110],['Remarks',null],['Actions',110]].map(([h,w])=>(
                                        <th key={h} className="px-3 py-2.5 text-left text-[11.5px] font-bold uppercase tracking-wide whitespace-nowrap border-r border-white/10 last:border-r-0" style={w?{width:w}:{}}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {docs.map((doc, i) => (
                                    <tr key={doc.DocumentID} className="hover:bg-[#EFF6FF] transition-colors duration-150 last:[&>td]:border-b-0">
                                        <td className="px-3 py-2.5 border-b border-gray-100 text-center font-semibold" style={{ color:PRIMARY }}>{i + 1}</td>
                                        <td className="px-3 py-2.5 border-b border-gray-100"><TypeBadge mime={doc.MimeType}/></td>
                                        <td className="px-3 py-2.5 border-b border-gray-100">
                                            <div className="flex items-center gap-1.5 font-medium text-gray-800 max-w-[240px] overflow-hidden text-ellipsis whitespace-nowrap">
                                                <FileIcon mime={doc.MimeType} size={15}/>
                                                <span title={doc.OriginalName || doc.FileName}>{doc.OriginalName || doc.FileName}</span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2.5 border-b border-gray-100 text-[13px] text-gray-700 font-medium">{doc.DocTypeName || doc.DocType || '—'}</td>
                                        <td className="px-3 py-2.5 border-b border-gray-100 text-right text-xs text-gray-500 font-mono">{fmtSize(doc.FileSize || 0)}</td>
                                        <td className="px-3 py-2.5 border-b border-gray-100">{fmtDate(doc.UploadedAt)}</td>
                                        <td className="px-3 py-2.5 border-b border-gray-100 text-xs text-gray-400 max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">{doc.Remarks || '—'}</td>
                                        <td className="px-3 py-2.5 border-b border-gray-100">
                                            <div className="flex gap-1.5 items-center">
                                                {canPreview(doc.MimeType) && <button onClick={() => handlePreview(doc)} title="Preview" className="bg-none border-0 cursor-pointer p-1.5 rounded-md inline-flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-150"><Eye size={14}/></button>}
                                                <button onClick={() => handleDownload(doc)} title="Download" className="bg-none border-0 cursor-pointer p-1.5 rounded-md inline-flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-150"><Download size={14}/></button>
                                                <button onClick={() => handleDelete(doc)} title="Delete" className="bg-none border-0 cursor-pointer p-1.5 rounded-md inline-flex items-center justify-center text-gray-400 hover:bg-[#FEE2E2] hover:text-[#DC2626] transition-colors duration-150"><Trash2 size={14}/></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <div className="text-xs text-gray-400 px-1 py-2.5 border-t border-gray-100">
                <strong>Allowed:</strong> PDF, JPG, PNG, GIF, DOC, DOCX, XLS, XLSX, TXT &nbsp;·&nbsp;
                <strong>Max size:</strong> {MAX_MB} MB per file
            </div>
        </div>
    );
};

export default DocumentsTab;